/**
 * Subscription API Endpoints
 * Handles billing portal and subscription management
 */

import { apiClient, getAuthHeaders } from "../client";
import type { RequestOptions, SubscriptionPortalSession } from "../types";

/**
 * Create a Stripe billing portal session for the authenticated user
 * Requires authentication (ID token)
 * @returns Portal session with redirect URL
 */
export async function createSubscriptionPortalSession(
  options?: RequestOptions,
): Promise<{ success: true; data: SubscriptionPortalSession } | { success: false; error: Error }> {
  try {
    const authHeaders = await getAuthHeaders();
    const result = await apiClient.post<SubscriptionPortalSession>(
      "/subscription/session",
      {},
      {
        ...options,
        headers: {
          ...authHeaders,
          ...options?.headers,
        },
      },
    );

    if (!result.success) {
      const errorMessage = result.error.message || "Failed to create subscription portal session";
      return {
        success: false,
        error: new Error(errorMessage),
      };
    }

    return {
      success: true,
      data: result.data!,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error("Unknown error"),
    };
  }
}

export const subscriptionApi = {
  createPortalSession: createSubscriptionPortalSession,
};
